'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useConfirmPopup } from '../../hooks/useConfirmPopup';
import { apiErrorCode, apiErrorMessage, formatApiError } from '../../utils/apiError';
import { showErrorToast } from '../../components/Toast';
import { needsInventoryRefresh, upgradeRefusal } from './upgradeErrors';
import Confetti from 'react-confetti-boom';
import { ScreenContent, ScreenFixedBottom, ScreenFixedTop } from '../../screen';
import { Button, ButtonGroup } from '../../components/ui';
import { Credits } from '../../components/Credits';
import { useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../contexts/ApiContext';
import { queryKeys } from '../../lib/queryClient';
import { playEarnings } from '../../lib/uiSounds';
import { useHaptic } from '../../hooks/useHaptic';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import type { AvailableUpgradeTargetItem, UpgradeProbabilityResponse, UpgradeResult } from '../../services/api';
import { UserInventoryItemResponseStatusEnum } from '../../services/generated/Api';
import { UpgradeSourceSheet } from './UpgradeSourceSheet';
import { UpgradeEntryCard } from './UpgradeEntryCard';
import { UpgradeTargetSheet } from './UpgradeTargetSheet';
import { UpgradeDesktopInventoryPanel, UpgradeDesktopTargetsPanel } from './UpgradeDesktopPanels';
import { DesktopFullBleed } from '../../contexts/DesktopContentWidthContext';
import { UpgradeFAQSheet } from './UpgradeFAQSheet';
import { UpgradeHistorySheet } from './UpgradeHistorySheet';
import { UpgradeSlot } from './UpgradeSlot';
import { UpgradePresets } from './UpgradePresets';
import { UpgradeSuccessResult } from './UpgradeSuccessResult';
import { DesktopResultLayout } from '../../components/DesktopResultLayout';
import {
  UpgradeDial,
  DIAL_SPIN_MS,
  DIAL_FAST_SPIN_MS,
  DIAL_FLASH_MS,
  DIAL_FAST_FLASH_MS,
  DIAL_WIN_MS,
  DIAL_FAST_WIN_MS,
} from './UpgradeDial';
import { useGameMotionEnabled, useUiMotionEnabled } from '../../hooks/useMotion';
import { isBigWin } from '../../utils/bigWin';
import { cn } from '../../utils/cn';
import type { ScreenParams } from '../../types/navigation';
import { extractSkinName, formatCreditsNumber } from '../../utils/formatters';
import translations from './UpgradeScreen.translations';
import { FaClock, FaQuestionCircle, FaShare, FaTrophy } from '../../components/icons';
import { UpgradeTopSheet, type UpgradeTopPeriod } from './UpgradeTopSheet';
import { useShareWin } from '../../hooks/useShareWin';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { useNavigation } from '../../contexts/NavigationContext';
import { MIN_UPGRADE_ITEM_PRICE, useUpgradeInventoryInfinite, useUpgradeTop } from '../../queries/useUpgradeQueries';
import './UpgradeScreen.css';

export interface UpgradeItem {
  id: string;
  hashName: string;
  name: string;
  imageUrl: string;
  rarityColor: string;
  rarityName?: string;
  price: number;
  steamPrice?: number | null;
  statTrak: boolean;
  souvenir: boolean;
}

interface UpgradeScreenProps {
  params?: ScreenParams & { itemId?: string };
  initialSourceItem?: UpgradeItem | null;
  initialSourceAmount?: number | null;
  initialTargetItem?: UpgradeItem | null;
  initialProbability?: UpgradeProbabilityResponse | null;
}

const SCENE_BACKGROUND =
  'radial-gradient(ellipse 55% 70% at 50% 40%, rgba(255,193,82,0.07), transparent 70%), linear-gradient(180deg, rgba(22,24,34,0.9) 0%, rgba(12,13,20,0.95) 100%)';

const RESULT_ACTIONS_DELAY_MS = 900;
const RESULT_ACTIONS_DELAY_BIG_MS = 2900;

const entryKey = (sourceItemId: string, sourceAmount: number | null, targetHashName: string) =>
  `${sourceItemId}+${sourceAmount ?? 0}>${targetHashName}`;

export const UpgradeScreen: React.FC<UpgradeScreenProps> = ({ params, initialSourceItem = null, initialSourceAmount = null, initialTargetItem = null, initialProbability = null }) => {
  const { t } = useLocalTranslation(translations);
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { impact, notification } = useHaptic();
  const isDesktop = useIsDesktop();
  const animationsEnabled = useGameMotionEnabled();
  const [sourceSheetOpen, setSourceSheetOpen] = useState(false);
  const [targetSheetOpen, setTargetSheetOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [topOpen, setTopOpen] = useState(false);
  const [topPeriod, setTopPeriod] = useState<UpgradeTopPeriod>('day');
  const upgradeTop = useUpgradeTop(topPeriod, topOpen);
  const [sourceItem, setSourceItem] = useState<UpgradeItem | null>(initialSourceItem);
  const [sourceAmount, setSourceAmount] = useState<number | null>(initialSourceAmount);
  const [targetItem, setTargetItem] = useState<UpgradeItem | null>(initialTargetItem);
  const [probability, setProbability] = useState<UpgradeProbabilityResponse | null>(initialProbability);
  const [probabilityKey, setProbabilityKey] = useState<string | null>(() =>
    initialProbability && initialSourceItem && initialTargetItem
      ? entryKey(initialSourceItem.id, initialSourceAmount, initialTargetItem.hashName)
      : null,
  );
  const probabilityRequestRef = useRef<{ key: string; result: Promise<UpgradeProbabilityResponse | null> } | null>(
    null,
  );
  const [isExecuting, setIsExecuting] = useState(false);
  const [isSelling, setIsSelling] = useState(false);
  const [upgradeResult, setUpgradeResult] = useState<UpgradeResult | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [spinPhase, setSpinPhase] = useState<'idle' | 'spinning' | 'stopped'>('idle');
  const spinPhaseRef = useRef(spinPhase);
  useEffect(() => {
    spinPhaseRef.current = spinPhase;
  }, [spinPhase]);
  const [spinResult, setSpinResult] = useState<'win' | 'lose' | null>(null);
  const [isLoadingSourceItem, setIsLoadingSourceItem] = useState(false);
  const [probabilityFailed, setProbabilityFailed] = useState(false);
  const [sourceLoadFailed, setSourceLoadFailed] = useState(false);
  const [hasLoadedFromParams, setHasLoadedFromParams] = useState(false);

  const showPopup = useConfirmPopup();
  const navigation = useNavigation();
  const pageScrollRef = useRef<HTMLDivElement | null>(null);

  const upgradeInventory = useUpgradeInventoryInfinite();
  const inventoryEmpty =
    upgradeInventory.isSuccess && (upgradeInventory.data.pages[0]?.items.length ?? 0) === 0;
  const hasSource = !!sourceItem;
  const probabilityIsCurrent =
    !!sourceItem && !!targetItem && probabilityKey === entryKey(sourceItem.id, sourceAmount, targetItem.hashName);
  const nothingToUpgrade = inventoryEmpty && !hasSource && !isLoadingSourceItem && !params?.itemId;

  const upgradeShareName = targetItem?.name ?? '';
  const upgradeSharePrice = targetItem?.price ?? 0;
  const upgradeSourcePrice = (sourceItem?.price ?? 0) + (sourceAmount ?? 0);
  const upgradeIsBigWin = isBigWin(upgradeSharePrice, upgradeSourcePrice);
  const upgradePriceStr = formatCreditsNumber(upgradeSharePrice);
  const upgradeInterp = { itemName: upgradeShareName, price: upgradePriceStr };

  const { share: handleShare, isSharing, isBigWin: shareIsBigWin } = useShareWin({
    inventoryItemId: upgradeResult?.resultItemId ?? '',
    itemName: upgradeShareName,
    itemPrice: upgradeSharePrice,
    itemHashName: targetItem?.hashName ?? upgradeShareName,
    sourcePrice: upgradeSourcePrice,
    caption: upgradeIsBigWin ? t('shareCaptionBigWin', upgradeInterp) : t('shareCaption', upgradeInterp),
    buttonText: t('shareButton'),
    fallbackText: t('shareFallback', upgradeInterp),
  });

  const loadSourceItem = useCallback(async () => {
      if (!params?.itemId || hasLoadedFromParams) return;

      setIsLoadingSourceItem(true);
      setSourceLoadFailed(false);
      try {
        const response = await apiClient.api.inventoryControllerGetInventoryItemDetails(params.itemId);
        setHasLoadedFromParams(true);
        if (response.status !== UserInventoryItemResponseStatusEnum.AVAILABLE) return;

        const upgradeItem: UpgradeItem = {
          id: response.id,
          hashName: response.item.hashName,
          name: response.item.name,
          imageUrl: response.item.imageUrl || '',
          rarityColor: response.item.rarityColor || '',
          price: response.acquiredPriceInCredits,
          statTrak: response.item.statTrak,
          souvenir: response.item.souvenir,
        };

        setSourceItem(upgradeItem);
      } catch (error) {
        console.error('Failed to load source item:', formatApiError(error));
        setSourceLoadFailed(true);
      } finally {
        setIsLoadingSourceItem(false);
      }
  }, [params?.itemId, apiClient, hasLoadedFromParams]);

  useEffect(() => {
    void loadSourceItem();
  }, [loadSourceItem]);

  const handleSlotClick = useCallback(
    (slotIndex: number) => {
      if (spinPhase !== 'idle' || isDesktop) return;
      if (slotIndex === 0) setSourceSheetOpen(true);
      else if (slotIndex === 1 && hasSource) setTargetSheetOpen(true);
    },
    [hasSource, isDesktop, spinPhase],
  );

  const calculateProbability = useCallback(
    (source: { item: UpgradeItem; amount: number | null }, target: UpgradeItem) => {
      const key = entryKey(source.item.id, source.amount, target.hashName);
      const result = (async () => {
        try {
          setProbabilityFailed(false);

          const response = await apiClient.api.upgradeControllerCalculateProbability({
            sourceItemId: source.item.id,
            sourceAmount: source.amount ?? undefined,
            targetItemId: target.hashName,
          });

          if (probabilityRequestRef.current?.key !== key) return null;
          setProbability(response);
          setProbabilityKey(key);
          return response;
        } catch (error) {
          if (probabilityRequestRef.current?.key !== key) return null;
          console.error('Failed to calculate probability:', formatApiError(error));
          setProbability(null);
          setProbabilityFailed(true);
          return null;
        }
      })();
      probabilityRequestRef.current = { key, result };
      return result;
    },
    [apiClient],
  );

  const handleSourceItemSelect = useCallback((item: UpgradeItem) => {
    setSourceItem(item);
    setTargetItem(null);
    setProbability(null);
    setProbabilityFailed(false);
  }, []);

  const handleSourceCreditsSelect = useCallback((amount: number | null) => {
    setSourceAmount(amount);
  }, []);

  const handleTargetItemSelect = useCallback(
    (item: AvailableUpgradeTargetItem) => {
      if (spinPhaseRef.current !== 'idle') return;
      const upgradeItem: UpgradeItem = {
        id: item.hashName,
        hashName: item.hashName,
        name: item.name,
        imageUrl: item.imageUrl || '',
        rarityColor: item.rarityColor,
        rarityName: item.rarityName || undefined,
        price: item.price || 0,
        steamPrice: item.steamPrice,
        statTrak: item.statTrak,
        souvenir: item.souvenir,
      };

      setTargetItem(upgradeItem);

      if (sourceItem) {
        calculateProbability({ item: sourceItem, amount: sourceAmount }, upgradeItem);
      }
    },
    [sourceItem, sourceAmount, calculateProbability],
  );

  useEffect(() => {
    if (!targetItem || !sourceItem || spinPhase !== 'idle') return;
    void calculateProbability({ item: sourceItem, amount: sourceAmount }, targetItem);
    // WHY: пересчёт привязан к самому входу, а calculateProbability пересоздаётся вместе с ним
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceItem?.id, sourceAmount, targetItem?.hashName]);

  const handleExecuteUpgrade = async () => {
    if (!sourceItem || !targetItem || !probability?.isAvailable) return;

    try {
      setIsExecuting(true);

      if (!probabilityIsCurrent) {
        const key = entryKey(sourceItem.id, sourceAmount, targetItem.hashName);
        const pending = probabilityRequestRef.current;
        const fresh = await (pending?.key === key
          ? pending.result
          : calculateProbability({ item: sourceItem, amount: sourceAmount }, targetItem));
        if (!fresh?.isAvailable || probabilityRequestRef.current?.key !== key) return;
      }

      impact('heavy');
      spinPhaseRef.current = 'spinning';
      setSpinPhase('spinning');
      setSpinResult(null);

      const [response] = await Promise.all([
        apiClient.api.upgradeControllerPerformItemUpgrade({
          sourceItemId: sourceItem.id,
          targetItemId: targetItem.hashName,
          sourceAmount: sourceAmount ?? undefined,
        }),
        new Promise((resolve) => setTimeout(resolve, 500)),
      ]);

      setUpgradeResult(response);
      setSpinResult(response.isSuccess ? 'win' : 'lose');

      const spinMs = animationsEnabled ? DIAL_SPIN_MS : DIAL_FAST_SPIN_MS;
      await new Promise((resolve) => setTimeout(resolve, spinMs));

      if (!response.isSuccess) {
        notification('error');
        await new Promise((resolve) => setTimeout(resolve, animationsEnabled ? DIAL_FLASH_MS : DIAL_FAST_FLASH_MS));
        setUpgradeResult(null);
        setSourceItem(null);
        setSourceAmount(null);
        setTargetItem(null);
        setProbability(null);
        setProbabilityFailed(false);
        setSpinPhase('idle');
        setSpinResult(null);
        void queryClient.invalidateQueries({ queryKey: ['upgrade'] });
        void queryClient.invalidateQueries({ queryKey: queryKeys.profile });
        return;
      }

      if (isBigWin(targetItem.price, upgradeSourcePrice)) impact('heavy');
      notification('success');
      void queryClient.invalidateQueries({ queryKey: queryKeys.profile });
      await new Promise((resolve) => setTimeout(resolve, animationsEnabled ? DIAL_WIN_MS : DIAL_FAST_WIN_MS));
      setSpinPhase('stopped');
      setShowResult(true);
    } catch (error) {
      console.error('Upgrade failed:', formatApiError(error));
      notification('error');
      setSpinPhase('idle');
      setSpinResult(null);

      const refusal = upgradeRefusal(error);
      const hint =
        refusal === 'item-used'
          ? t('upgradeFailedItemUsed')
          : refusal === 'item-gone'
            ? t('upgradeFailedItemGone')
            : refusal === 'pair-rejected'
              ? t('upgradeFailedPair')
              : refusal === 'no-funds'
                ? t('upgradeFailedNoFunds')
                : t('upgradeFailedHint');

      showErrorToast(t('upgradeFailedTitle'), hint, 'upgrade-failed');

      if (needsInventoryRefresh(refusal)) {
        setSourceItem(null);
        setSourceAmount(null);
        setTargetItem(null);
        setProbability(null);
        setProbabilityFailed(false);
        void queryClient.invalidateQueries({ queryKey: ['upgrade'] });
      }
      if (refusal === 'no-funds') void queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    } finally {
      setIsExecuting(false);
    }
  };

  const handleTryAgain = () => {
    setShowResult(false);
    setUpgradeResult(null);
    setSourceItem(null);
    setSourceAmount(null);
    setTargetItem(null);
    setProbability(null);
    setProbabilityFailed(false);
    setSpinPhase('idle');
    setSpinResult(null);
  };

  const handleContinueUpgrade = () => {
    if (!targetItem || !upgradeResult?.resultItemId) return;
    const won: UpgradeItem = { ...targetItem, id: upgradeResult.resultItemId };
    setShowResult(false);
    setUpgradeResult(null);
    setSourceItem(won);
    setSourceAmount(null);
    setTargetItem(null);
    setProbability(null);
    setProbabilityFailed(false);
    setSpinPhase('idle');
    setSpinResult(null);
    void queryClient.invalidateQueries({ queryKey: ['upgrade'] });
  };

  const canExecute = hasSource && targetItem && probability?.isAvailable && !isExecuting && spinPhase === 'idle';

  const uiMotionEnabled = useUiMotionEnabled();
  const [resultActionsRevealed, setResultActionsRevealed] = useState(false);
  useEffect(() => {
    if (!showResult) {
      setResultActionsRevealed(false);
      return;
    }
    if (!uiMotionEnabled) {
      setResultActionsRevealed(true);
      return;
    }
    const delay = upgradeIsBigWin ? RESULT_ACTIONS_DELAY_BIG_MS : RESULT_ACTIONS_DELAY_MS;
    const id = setTimeout(() => setResultActionsRevealed(true), delay);
    return () => clearTimeout(id);
  }, [showResult, uiMotionEnabled, upgradeIsBigWin]);

  const resultActions =
    showResult && upgradeResult ? (
      upgradeResult.isSuccess && targetItem && upgradeResult.resultItemId ? (
        <>
          <Button
            onClick={handleContinueUpgrade}
            variant="hero"
            size="l"
            stretched
            data-sound="cta"
            data-testid="upgrade-continue"
          >
            {t('continueUpgrade')}
          </Button>
          <Button
            variant="secondary"
            size="l"
            stretched
            loading={isSelling}
            onClick={async () => {
              if (isSelling) return;
              const result = await showPopup({
                title: t('confirmSellTitle'),
                message: t('confirmSellMessage', {
                  item: extractSkinName(targetItem.name),
                  price: formatCreditsNumber(targetItem.price),
                }),
                buttons: [
                  { id: 'cancel', type: 'cancel' },
                  { id: 'confirm', type: 'default', text: t('confirmSellConfirm') },
                ],
              });
              if (result !== 'confirm') return;

              setIsSelling(true);
              try {
                await apiClient.api.inventoryControllerSellInventoryItem(upgradeResult.resultItemId!);
                playEarnings();
                queryClient.invalidateQueries({ queryKey: queryKeys.profile });
                handleTryAgain();
              } catch (err) {
                if (apiErrorCode(err) === 'ITEM_NOT_AVAILABLE') {
                  playEarnings();
                  queryClient.invalidateQueries({ queryKey: queryKeys.profile });
                  handleTryAgain();
                  return;
                }
                notification('error');
                console.error('Failed to sell item:', formatApiError(err));
                showErrorToast(t('sellFailedTitle'), apiErrorMessage(err));
              } finally {
                setIsSelling(false);
              }
            }}
          >
            {t('sell')} <Credits amount={targetItem.price} />
          </Button>
          <Button
            size="l"
            stretched
            loading={isSharing}
            before={<FaShare size={16} />}
            onClick={handleShare}
            variant={shareIsBigWin ? 'hero' : 'share'}
          >
            {shareIsBigWin ? t('shareBigWin') : t('share')}
          </Button>
        </>
      ) : (
        <Button onClick={handleTryAgain} variant="cta" size="l" stretched>
          {t('tryAgain')}
        </Button>
      )
    ) : null;

  const resultActionsBar = resultActions ? (
    <div className={resultActionsRevealed ? 'animate-shot-result-actions-in' : 'invisible'}>
      <ButtonGroup>{resultActions}</ButtonGroup>
    </div>
  ) : null;

  const dialNode = (
    <UpgradeDial
      probability={probability?.probability ?? null}
      coefficient={probability?.coefficient ?? null}
      emptyHint={
        nothingToUpgrade
          ? t('noSuitableItems')
          : !hasSource
            ? t('selectSourceItem')
            : !targetItem
              ? t('selectTargetItem')
              : undefined
      }
      onHintRetry={
        probabilityFailed && sourceItem && targetItem
          ? () => void calculateProbability({ item: sourceItem, amount: sourceAmount }, targetItem)
          : undefined
      }
      isSpinning={spinPhase === 'spinning'}
      spinResult={spinResult}
      animationsEnabled={animationsEnabled}
      size={isDesktop ? 280 : 208}
    />
  );

  const presetsNode = (
    <UpgradePresets
      sourceItemId={sourceItem?.id ?? null}
      sourceAmount={sourceAmount}
      selectedHashName={targetItem?.hashName ?? null}
      onPick={handleTargetItemSelect}
      disabled={spinPhase !== 'idle'}
    />
  );

  const emptyNode = (
    <div className="flex flex-col items-center gap-4 text-center">
      <p className="m-0 max-w-[320px] text-sm text-label-secondary">
        {t('noSuitableItemsHint', { min: formatCreditsNumber(MIN_UPGRADE_ITEM_PRICE) })}
      </p>
      <Button size="l" variant="cta" onClick={() => navigation.navigate('cases')} data-testid="upgrade-empty-go-cases">
        {t('goToCases')}
      </Button>
    </div>
  );

  const entryCardNode = (
    <UpgradeEntryCard
      item={sourceItem}
      amount={sourceAmount}
      targetPrice={targetItem?.price ?? null}
      disabled={spinPhase !== 'idle'}
      onChange={handleSourceCreditsSelect}
    />
  );

  const executeButton = (
    <Button
      size="l"
      stretched
      variant="hero"
      onClick={handleExecuteUpgrade}
      disabled={!canExecute}
      loading={isExecuting}
      data-sound="cta"
      data-testid="upgrade-execute"
    >
      {t('dialUpgrade')}
    </Button>
  );

  const headerButtonClass = 'group h-11 w-11 bg-transparent p-0 text-white/60 hover:text-white/90';
  const headerButtonInnerClass =
    'grid h-8 w-8 place-items-center rounded-full border border-white/[0.1] bg-white/[0.06] transition-colors group-hover:bg-white/[0.12]';

  const headerButtons = (
    <>
      <Button variant="tertiary" data-sound="menu_select" data-testid="upgrade-top-button" aria-label={t('topTitle')} className={headerButtonClass} onClick={() => setTopOpen(true)}>
        <span className={headerButtonInnerClass}>
          <FaTrophy size={14} />
        </span>
      </Button>
      <Button variant="tertiary" data-sound="menu_select" data-testid="upgrade-history-button" aria-label={t('history')} className={headerButtonClass} onClick={() => setHistoryOpen(true)}>
        <span className={headerButtonInnerClass}>
          <FaClock size={15} />
        </span>
      </Button>
      <Button variant="tertiary" data-sound="menu_select" data-testid="upgrade-faq-button" aria-label={t('faqHowItWorks')} className={headerButtonClass} onClick={() => setFaqOpen(true)}>
        <span className={headerButtonInnerClass}>
          <FaQuestionCircle size={16} />
        </span>
      </Button>
    </>
  );

  return (
    <>

      {isDesktop && !showResult && <DesktopFullBleed />}
      {!showResult && !isDesktop && (
        <ScreenFixedTop title={t('screenTitle')} />
      )}
      {showResult && upgradeResult?.isSuccess && targetItem && !upgradeIsBigWin && (
        <div className="fixed inset-0 z-50 pointer-events-none">
          <Confetti
            mode="fall"
            particleCount={30}
            colors={['#ff577f', '#ff884b', '#ffd384', '#fff9b0', '#3498db']}
            shapeSize={10}
          />
        </div>
      )}
      {(!isDesktop || !upgradeResult?.isSuccess) && (
        <ScreenFixedBottom showBlur={!!resultActionsBar && resultActionsRevealed}>{resultActionsBar}</ScreenFixedBottom>
      )}
      <ScreenContent className="flex-1 flex flex-col" scrollRef={pageScrollRef}>
        {showResult && upgradeResult && upgradeResult.isSuccess && targetItem ? (
          isDesktop ? (
            <DesktopResultLayout
              card={<UpgradeSuccessResult item={targetItem} bigWin={upgradeIsBigWin} />}
              actions={resultActionsBar}
            />
          ) : (
            <div className="w-full">
              <UpgradeSuccessResult item={targetItem} bigWin={upgradeIsBigWin} />
            </div>
          )
        ) : (
          isDesktop ? (
            <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-6 p-6">
              <section
                data-testid="upgrade-scene"
                className="relative shrink-0 rounded-[28px] border border-white/[0.07] px-8 pt-7 pb-6"
                style={{ background: SCENE_BACKGROUND }}
              >
                {nothingToUpgrade && <div className="absolute end-3 top-3 flex items-center gap-1">{headerButtons}</div>}
                <div className={cn('grid items-center gap-8', !nothingToUpgrade && 'grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]')}>
                  {!nothingToUpgrade && (
                    <UpgradeSlot
                      item={sourceItem}
                      type="input"
                      height={280}
                      disabled={spinPhase !== 'idle'}
                      loading={isLoadingSourceItem}
                      error={sourceLoadFailed}
                      onClick={sourceLoadFailed ? () => void loadSourceItem() : () => handleSlotClick(0)}
                    />
                  )}
                  <div className="flex flex-col items-center gap-4">
                    {dialNode}
                    {nothingToUpgrade && emptyNode}
                  </div>
                  {!nothingToUpgrade && (
                    <UpgradeSlot
                      item={targetItem}
                      type="output"
                      height={280}
                      disabled={!hasSource || spinPhase !== 'idle'}
                      pulse={spinPhase === 'spinning'}
                      onClick={() => hasSource && handleSlotClick(1)}
                    />
                  )}
                </div>
                {!nothingToUpgrade && (
                  <div className="mt-6 grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_220px_auto] items-end gap-4">
                    {entryCardNode}
                    {presetsNode}
                    <div className="pb-0.5">{executeButton}</div>
                    <div className="flex items-center pb-1">{headerButtons}</div>
                  </div>
                )}
              </section>

              {!nothingToUpgrade && (
                <div className="grid grid-cols-2 items-start gap-6">
                  <UpgradeDesktopInventoryPanel
                    onSelectItem={handleSourceItemSelect}
                    scrollRef={pageScrollRef}
                    selectedId={sourceItem?.id ?? null}
                    disabled={spinPhase !== 'idle'}
                  />
                  <UpgradeDesktopTargetsPanel
                    sourceItemId={sourceItem?.id ?? null}
                    sourceAmount={sourceAmount}
                    selectedHashName={targetItem?.hashName ?? null}
                    onSelectItem={handleTargetItemSelect}
                    scrollRef={pageScrollRef}
                    disabled={spinPhase !== 'idle'}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="relative mx-auto flex w-full min-w-0 flex-1 flex-col items-center gap-3 px-4 pt-1">
              <div className="absolute end-1 top-0 flex flex-col items-center">{headerButtons}</div>
              {dialNode}


              {nothingToUpgrade ? (
                emptyNode
              ) : (
                <div className="grid w-full grid-cols-2 gap-3">
                  <UpgradeSlot
                    item={sourceItem}
                    height={126}
                    type="input"
                    disabled={spinPhase !== 'idle'}
                    loading={isLoadingSourceItem}
                    error={sourceLoadFailed}
                    onClick={sourceLoadFailed ? () => void loadSourceItem() : () => handleSlotClick(0)}
                  />
                  <UpgradeSlot
                    item={targetItem}
                    type="output"
                    height={126}
                    disabled={!hasSource || spinPhase !== 'idle'}
                    pulse={spinPhase === 'spinning'}
                    onClick={() => hasSource && handleSlotClick(1)}
                  />
                </div>
              )}

              {!nothingToUpgrade && <div className="w-full">{presetsNode}</div>}
              {entryCardNode}
              {!nothingToUpgrade && executeButton}
            </div>
          )
        )}
      </ScreenContent>
      {!isDesktop && (
        <>
          <UpgradeSourceSheet
            isOpen={sourceSheetOpen}
            onClose={() => setSourceSheetOpen(false)}
            onSelectItem={handleSourceItemSelect}
          />
          {sourceItem && (
            <UpgradeTargetSheet
              isOpen={targetSheetOpen}
              onClose={() => setTargetSheetOpen(false)}
              onSelectItem={handleTargetItemSelect}
              sourceItemId={sourceItem.id}
              sourceAmount={sourceAmount}
            />
          )}
        </>
      )}
      <UpgradeFAQSheet isOpen={faqOpen} onClose={() => setFaqOpen(false)} />
      <UpgradeHistorySheet isOpen={historyOpen} onClose={() => setHistoryOpen(false)} />
      <UpgradeTopSheet
        isOpen={topOpen}
        onClose={() => setTopOpen(false)}
        period={topPeriod}
        onPeriodChange={setTopPeriod}
        data={upgradeTop.data}
        isLoading={upgradeTop.isLoading}
        isError={upgradeTop.isError}
        onRetry={() => void upgradeTop.refetch()}
        onUserClick={(userId) => {
          setTopOpen(false);
          navigation.navigate('profile', { userId });
        }}
      />
    </>
  );
};
